import { defineComponent as _defineComponent } from 'vue'
import FormLayout from '../common/FormLayout.vue'
import AssetQuantityChange from '@/components/inventory/AssetQuantityChange.vue'
import InventoryAdjustment, {
  InventoryQuantityChangeModel,
} from '@/models/inventory_adjustment'
import { computed, nextTick, onBeforeMount, ref } from 'vue'
import { useStore } from 'vuex-composition-helpers'
import { Asset, AssetQuantityCondition } from '@/models/asset'
import { cloneDeep } from 'lodash'
import VsForm from '@/components/vision/VsForm.vue'
import Big from 'big.js'
import LocationInventory from '@/models/location_inventory'


export default /*#__PURE__*/_defineComponent({
  __name: 'FieldViewAdjustmentForm',
  props: {
    locationId: null,
    assetId: null
  },
  emits: ['on-dismiss'],
  setup(__props: any, { emit }) {

const props = __props as {
  locationId: string | number
  assetId: number
}



const isSaving = ref(false)
const errorMessage = ref<string | null>(null)
const inventoryAdjustment = ref(new InventoryAdjustment())
const form = ref<VsForm>()
const isCreatingNewInventory = ref<boolean>(false)

const store = useStore()

const allAssets = computed<{ [key: string]: Asset }>(
  () => store.state.assets.all
)
const getById = computed<(id: string | number) => LocationInventory>(
  () => store.getters['inventory/getById']
)
async function adjustInventory(changes: object) {
  await store.dispatch('inventory/adjustInventory', changes)
}

const inventoryTypeOfAsset = computed<string>(() => {
  return allAssets.value[props.assetId]?.name ?? ''
})
const formTitle = computed<string>(() => {
  return `${isCreatingNewInventory.value ? 'New' : 'Edit'} ${
    inventoryTypeOfAsset.value
  } Inventory`
})
const addItemLabel = computed<string>(() => {
  return `Add ${inventoryTypeOfAsset.value} item`
})
const defaultAssets = computed<AssetQuantityCondition[]>(() => {
  return (
    getById.value(props.locationId)?.assets[props.assetId]?.conditions || []
  )
})

const canAddItem = computed<boolean>(() => {
  return (
    inventoryAdjustment.value.quantityChanges.length <
    getAllSubAssets.value.length
  )
})
const getAllSubAssets = computed<AssetQuantityCondition[]>(() => {
  if (props.assetId == null) return []
  return allAssets.value[props.assetId].conditions
})

const takenSubAssetIds = computed(
  () =>
    inventoryAdjustment.value.quantityChanges
      .map((qc) => qc.assetConditionId)
      .filter((aid) => aid != null) as number[]
)

onBeforeMount(() => {
  inventoryAdjustment.value.locationId = parseInt(
    props.locationId.toString(),
    10
  )
  inventoryAdjustment.value.assetId = props.assetId
  inventoryAdjustment.value.quantityChanges = []
  const existingInventory = defaultAssets.value.filter(
    (asset) => asset?.id && quantityOfAsset(asset?.id) !== 0
  )
  for (const inventory of existingInventory) {
    const quantityChange = new InventoryQuantityChangeModel()
    quantityChange.assetConditionId = inventory.id
    quantityChange.quantityChange = 0
    inventoryAdjustment.value.quantityChanges.push(quantityChange)
  }
  if (inventoryAdjustment.value.quantityChanges.length === 0) {
    isCreatingNewInventory.value = true
    addQuantityChange()
  }
})

const quantityOfAsset = (assetId: number) => {
  return defaultAssets.value.find((a) => a.id === assetId)?.quantity || 0
}

const handleChangeQuantity = (
  newQuantity: number,
  item: InventoryQuantityChangeModel
) => {
  const newQuantityPrecise = new Big(newQuantity)
  const quantityOfAssetPrecise = new Big(
    quantityOfAsset(item.assetConditionId ?? 0)
  )
  item.quantityChange = newQuantityPrecise
    .minus(quantityOfAssetPrecise)
    .toNumber()
}

const assetQuantityChangeRef = ref<AssetQuantityChange[]>([])

const focusEmptyInventoryAutoComplete = () => {
  const inventoryIndex: number =
    inventoryAdjustment.value.quantityChanges.findIndex(
      (i) => i.assetConditionId === null
    )
  const fieldRef = assetQuantityChangeRef.value[inventoryIndex]
  fieldRef?.focusAssetConditionId()
}

const addQuantityChange = () => {
  if (
    inventoryAdjustment.value.quantityChanges.some(
      (i) => i.assetConditionId === null
    )
  ) {
    focusEmptyInventoryAutoComplete()
  } else {
    inventoryAdjustment.value.addQuantityChange()
    nextTick(() => {
      focusEmptyInventoryAutoComplete()
    })
  }
}
const handleRemove = (index: number) => {
  inventoryAdjustment.value.quantityChanges.splice(index, 1)
}

const hasChanges = computed<boolean>(
  () =>
    inventoryAdjustment.value.quantityChanges.some(
      (qc) => qc.quantityChange !== 0
    ) || (inventoryAdjustment.value.reason?.length ?? 0) > 0
)

const clearError = () => {
  errorMessage.value = null
}



const submit = async () => {
  if (!isSaving.value && form.value != null && form.value.isValid()) {
    clearError()
    isSaving.value = true
    try {
      const changes = cloneDeep<InventoryAdjustment>(inventoryAdjustment.value)
      changes.quantityChanges = changes.quantityChanges.filter(
        (qa) => qa.quantityChange !== 0
      )
      await adjustInventory(changes)
    } catch {
      errorMessage.value = 'Something went wrong, please try again'
      return
    } finally {
      isSaving.value = false
    }

    emit('on-dismiss')
  }
}

const handleDismiss = () => {
  if (hasChanges.value) {
    const confirmation = window.confirm(
      'There are unsaved changes, are you sure you want to discard them?'
    )

    if (!confirmation) {
      return
    }
  }

  emit('on-dismiss')
}

return { __sfc: true,props, isSaving, errorMessage, inventoryAdjustment, form, isCreatingNewInventory, store, allAssets, getById, adjustInventory, inventoryTypeOfAsset, formTitle, addItemLabel, defaultAssets, canAddItem, getAllSubAssets, takenSubAssetIds, quantityOfAsset, handleChangeQuantity, assetQuantityChangeRef, focusEmptyInventoryAutoComplete, addQuantityChange, handleRemove, hasChanges, clearError, emit, submit, handleDismiss, FormLayout, AssetQuantityChange, VsForm }
}

})